/**
 * The Lecture Record's rules — SRS I5, docs/06-UI-SPEC.md §11.
 *
 * Kept away from React so the sorting can be tested properly. The owner asked for
 * Windows Explorer behaviour by name, and Explorer has a particular habit: clicking a
 * column sorts by it, clicking the same column again turns it round, and the rows
 * that tie keep the order they were already in.
 */

/** One finished lesson, as the list shows it. */
export interface Lecture {
  readonly id: string;
  readonly filename: string;
  readonly topic: string;
  readonly participantNames: readonly string[];
  readonly pageCount: number;
  readonly durationMinutes: number;
  /** Epoch milliseconds. */
  readonly endedAt: number;
  readonly sizeBytes: number;
  readonly isStarred: boolean;
  readonly isHot: boolean;
  readonly hasThumbnail: boolean;
}

/** The columns of the Details view, in the order docs/06-UI-SPEC.md §11.1 shows them. */
export type LectureColumn = 'name' | 'date' | 'duration' | 'students' | 'size';

export type SortDirection = 'ascending' | 'descending';

export interface SortOrder {
  readonly column: LectureColumn;
  readonly direction: SortDirection;
}

/** The three ways of looking at the list. */
export type LectureView = 'details' | 'large' | 'extraLarge';

/** What the list is sorted by before anybody clicks anything: newest lesson first. */
export const DEFAULT_SORT: SortOrder = { column: 'date', direction: 'descending' };

export const COLUMN_LABELS: Readonly<Record<LectureColumn, string>> = {
  name: 'Name',
  date: 'Date Modified',
  duration: 'Duration',
  students: 'Students',
  size: 'Size',
};

/**
 * What clicking a column header does.
 *
 * A new column starts ascending, which is what Explorer does — A to Z, oldest first,
 * smallest first. Clicking the column you are already sorted by turns it round.
 */
export function nextSort(current: SortOrder, clicked: LectureColumn): SortOrder {
  if (current.column !== clicked) return { column: clicked, direction: 'ascending' };
  return {
    column: clicked,
    direction: current.direction === 'ascending' ? 'descending' : 'ascending',
  };
}

/** Compares two lessons by one column. Text compares the way a person reads it. */
function compareBy(column: LectureColumn, a: Lecture, b: Lecture): number {
  switch (column) {
    case 'name':
      return a.filename.localeCompare(b.filename, undefined, { numeric: true });
    case 'date':
      return a.endedAt - b.endedAt;
    case 'duration':
      return a.durationMinutes - b.durationMinutes;
    case 'students':
      return studentsLabel(a).localeCompare(studentsLabel(b), undefined, { numeric: true });
    case 'size':
      return a.sizeBytes - b.sizeBytes;
  }
}

/**
 * The lessons in the order asked for.
 *
 * A copy, never the list itself, and stable: two lessons that tie on the column keep
 * the order they arrived in, so sorting by duration twice does not shuffle the rows
 * that are the same length.
 */
export function sortLectures(
  lectures: readonly Lecture[],
  order: SortOrder,
): readonly Lecture[] {
  const sign = order.direction === 'ascending' ? 1 : -1;
  return [...lectures].sort((a, b) => sign * compareBy(order.column, a, b));
}

/** The students column: the names, or a plain dash when nobody was named. */
export function studentsLabel(lecture: Lecture): string {
  return lecture.participantNames.length === 0 ? '—' : lecture.participantNames.join(', ');
}

/** `60 min`, the way docs/06-UI-SPEC.md §11.1 writes it. */
export function durationLabel(lecture: Lecture): string {
  return `${lecture.durationMinutes} min`;
}

/** `2.4 MB` — a size a person reads, not a number of bytes. */
export function sizeLabel(lecture: Lecture): string {
  const kilobytes = lecture.sizeBytes / 1024;
  if (kilobytes < 1) return `${lecture.sizeBytes} B`;
  if (kilobytes < 1024) return `${Math.round(kilobytes)} KB`;
  return `${(kilobytes / 1024).toFixed(1)} MB`;
}

/** `22 Aug 06:00` — the date and time a lesson finished. */
export function dateLabel(lecture: Lecture): string {
  const when = new Date(lecture.endedAt);
  const parts = new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(when);
  const get = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((part) => part.type === type)?.value ?? '';

  return `${get('day')} ${get('month').replace('.', '')} ${get('hour')}:${get('minute')}`;
}

/** Where to fetch one lesson's preview picture. */
export function thumbnailUrl(lecture: Lecture): string {
  return `/api/lectures/${lecture.id}/thumbnail`;
}
